import { useEffect, useLayoutEffect, useRef, useState, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getStream, StreamIdleTimeoutError } from '@/utils/api-handler';
import { refreshCommandMessagesPageOne } from '@/hooks/use-command-messages';
import { ExtendedChatMessage } from './chat-types';
import { mapMessagesAssistantById } from './message-utils';
import { createEventHandler, supervisedChunkHandler, type SSEEventHandler } from './sse-event-handler';
import { newestGeneratingAssistantId, readRunState, StreamSupervisor, type StreamEnd } from './stream-supervisor';
import type { ContextUsage } from '@/types/command-agent';

interface UseStreamReconnectArgs {
    agentId: string | null | undefined;
    sessionId: string | null | undefined;
    /** Session-level GENERATING flag (from the session list endpoint). */
    sessionStreamActive: boolean;
    /** Message-level GENERATING id (from the messages list endpoint) — takes precedence over `sessionStreamActive` for targeting. */
    generatingAssistantId: string | null;
    isLoadingMessages: boolean;
    /** Live ref of fetched messages so pagination re-renders don't re-trigger the effect. */
    fetchedMessagesRef: MutableRefObject<ExtendedChatMessage[]>;
    isTypingRef: MutableRefObject<boolean>;
    streamingMessageIdRef: MutableRefObject<string | null>;
    /** Lock state, so a GENERATING row seen while a stream held the lock is attached once it frees up. */
    isTyping?: boolean;
    streamingMessageId?: string | null;
    streamAbortRef: MutableRefObject<Set<AbortController>>;
    setMessages: Dispatch<SetStateAction<ExtendedChatMessage[]>>;
    setIsTyping: Dispatch<SetStateAction<boolean>>;
    setStreamingMessageId: Dispatch<SetStateAction<string | null>>;
    setStreamingPhase: Dispatch<SetStateAction<'working' | 'responding'>>;
    onSessionTitle?: (sessionId: string, title: string) => void;
    queryClient: QueryClient;
    onContextUsage?: (usage: ContextUsage) => void;
}

/** When only the session row says GENERATING, look for the run's message row again after these delays. */
const SESSION_ONLY_RECHECK_MS = [1_500, 4_000];
/** A page-1 read without an answer by then counts as unreachable; axios has no timeout of its own. */
const SERVER_READ_TIMEOUT_MS = 15_000;

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
    return new Promise<T>((resolve) => {
        const timer = setTimeout(() => resolve(fallback), ms);
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            () => {
                clearTimeout(timer);
                resolve(fallback);
            },
        );
    });
}

type CachedMessagesPages = { pages?: Array<{ items?: Array<{ id?: string; status?: string }> } | undefined> };

const hasVisibleContent = (msg: ExtendedChatMessage) =>
    (typeof msg.content === 'string' && msg.content.trim().length > 0) ||
    (msg.contentSegments?.length ?? 0) > 0 ||
    (msg.tools?.length ?? 0) > 0 ||
    (typeof msg.reasoning === 'string' && msg.reasoning.trim().length > 0) ||
    !!msg.streamError;

function findAssistant(list: ExtendedChatMessage[], assistantId: string): ExtendedChatMessage | undefined {
    for (const msg of list) {
        if (msg.id === assistantId) return msg;
        const branch = msg.branches?.find((b) => b.id === assistantId);
        if (branch) return branch;
    }
    return undefined;
}

const isPageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

function createStreamSupervisor(latest: MutableRefObject<UseStreamReconnectArgs>): StreamSupervisor {
    const supervisor: StreamSupervisor = new StreamSupervisor({
        now: () => Date.now(),
        setTimer: (callback, ms) => setTimeout(callback, ms),
        clearTimer: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
        random: () => Math.random(),
        isPageHidden,
        fetchServerState: async (target) => {
            const page = await withTimeout(
                refreshCommandMessagesPageOne(latest.current.queryClient, target.agentId, target.sessionId),
                SERVER_READ_TIMEOUT_MS,
                null,
            );
            return page ? readRunState(page.items ?? [], target) : { status: 'unreachable' };
        },
        // Read the query cache itself: fetchedMessagesRef only catches up on the next render.
        isGeneratingInCache: (target) =>
            latest.current.queryClient
                .getQueriesData<CachedMessagesPages>({
                    predicate: (query) => query.queryKey[0] === 'command-messages' && query.queryKey[2] === target.sessionId,
                })
                .some(([, data]) =>
                    data?.pages?.some((page) =>
                        page?.items?.some((item) => item?.id === target.assistantId && item?.status === 'GENERATING'),
                    ),
                ),
        reattach: (record) => {
            const { setMessages, onSessionTitle, setStreamingPhase, onContextUsage, streamAbortRef } = latest.current;
            const { agentId, sessionId, assistantId, userMessageId } = record.target;
            // GET .../stream replays the run from message_start, so a fresh handler rebuilds the whole bubble.
            const eventHandler = createEventHandler({
                systemMessageId: assistantId,
                setMessages,
                onSessionTitle,
                cancelledRef: record.cancelledRef,
                setStreamingPhase,
                userMessageId,
                onAssistantIdResolved: (realId) => supervisor.noteAssistantId(record, realId),
                onUserIdResolved: (realId) => {
                    record.target.userMessageId = realId;
                },
                onContextUsage,
            });
            // A replay opens with message_start. Without it the server's buffer no longer covers this
            // run (finished over 30 minutes ago, or trimmed), and applying the tail would wipe the bubble.
            let replaying = false;
            const replayHandler: SSEEventHandler = (event) => {
                if (!replaying && event?.type !== 'message_start') return;
                replaying = true;
                eventHandler(event);
            };
            streamAbortRef.current.add(record.controller);
            return getStream(
                `/api/command-team/agents/${agentId}/sessions/${sessionId}/messages/stream`,
                supervisedChunkHandler(supervisor, record, replayHandler),
                record.controller.signal,
                () => supervisor.noteActivity(record),
            )
                .catch((error: unknown): StreamEnd => {
                    if (error instanceof StreamIdleTimeoutError) return { reason: 'idle_timeout', responseStarted: true };
                    throw error;
                })
                .finally(() => {
                    streamAbortRef.current.delete(record.controller);
                });
        },
        adoptAssistantId: (fromId, toId) => {
            latest.current.setMessages((prev) => {
                if (!findAssistant(prev, fromId)) return prev;
                // The merge may already show the server's copy of the run; keep ours, it has the streamed content.
                const withoutCopy = prev.filter((msg) => msg.id !== toId);
                return mapMessagesAssistantById(withoutCopy, fromId, (msg) => ({ ...msg, id: toId, dbId: toId }));
            });
        },
        showRecovering: (target) => {
            const { setIsTyping, setStreamingMessageId, setStreamingPhase, setMessages } = latest.current;
            setIsTyping(true);
            // Keep an id that already holds the lock: a temp-regen-* id is what pauses merges during regenerate.
            setStreamingMessageId((current) => current ?? target.assistantId);
            setStreamingPhase('working');
            setMessages((prev) =>
                mapMessagesAssistantById(prev, target.assistantId, (msg) => (msg.isStreaming ? msg : { ...msg, isStreaming: true })),
            );
        },
        finishRecovery: (target, end, ownsLock) => {
            const { setMessages, setIsTyping, setStreamingMessageId, setStreamingPhase, streamingMessageIdRef, queryClient } =
                latest.current;
            setMessages((prev) => {
                const msg = findAssistant(prev, target.assistantId);
                if (!msg) return prev;
                // A temp bubble that never received anything has no server row behind it.
                if (target.assistantId.startsWith('temp-') && !hasVisibleContent(msg) && prev.includes(msg)) {
                    return prev.filter((m) => m !== msg);
                }
                return mapMessagesAssistantById(prev, target.assistantId, (m) => ({ ...m, isStreaming: false, status: 'COMPLETED' }));
            });
            if (ownsLock) {
                const releaseLock = () => {
                    setIsTyping(false);
                    setStreamingMessageId(null);
                    setStreamingPhase('working');
                };
                // A regenerate keeps merges paused until the cache holds its new branch, like its normal finish.
                if (streamingMessageIdRef.current?.startsWith('temp-regen-')) {
                    void withTimeout(
                        refreshCommandMessagesPageOne(queryClient, target.agentId, target.sessionId),
                        SERVER_READ_TIMEOUT_MS,
                        null,
                    ).then(releaseLock);
                } else {
                    releaseLock();
                }
            }
            if (end === 'gave_up' && !isPageHidden()) {
                toast.warning('Lost connection to this reply', {
                    description: "It may still be running. We'll check again when you come back, or reconnect now.",
                    action: { label: 'Reconnect', onClick: () => supervisor.reconnect(target) },
                });
            } else if (end === 'lost') {
                toast.error("Couldn't send your message", {
                    description: target.failureMessage || 'Check your connection and try again.',
                });
            }
        },
        revalidate: () => {
            const { agentId, sessionId, sessionStreamActive, fetchedMessagesRef, queryClient, isTypingRef, streamingMessageIdRef } =
                latest.current;
            if (!agentId || !sessionId) return;
            // A consumer stream outside the supervisor (legacy regenerate) still holds the lock.
            if (isTypingRef.current || streamingMessageIdRef.current) return;
            const cachedGenerating = fetchedMessagesRef.current.some(
                (msg) => msg.role === 'assistant' && msg.status === 'GENERATING',
            );
            if (!cachedGenerating && !sessionStreamActive) return;
            void refreshCommandMessagesPageOne(queryClient, agentId, sessionId).then((page) => {
                const generatingId = page ? newestGeneratingAssistantId(page.items ?? []) : null;
                if (!generatingId || latest.current.isTypingRef.current || latest.current.streamingMessageIdRef.current) return;
                supervisor.autoAttach({ agentId, sessionId, assistantId: generatingId });
            });
        },
    });
    return supervisor;
}

/**
 * Keeps the open session's reply attached to its server-side run. Re-attaches the SSE stream to
 * an assistant row the server reports GENERATING (reload, chat switch, discarded tab), and returns
 * the `StreamSupervisor` that sends/regenerates register with, so a stream that drops, freezes in
 * a background tab or goes idle is recovered instead of being shown as finished.
 */
export function useStreamReconnect(args: UseStreamReconnectArgs): StreamSupervisor {
    const {
        agentId,
        sessionId,
        sessionStreamActive,
        generatingAssistantId,
        isLoadingMessages,
        fetchedMessagesRef,
        isTypingRef,
        streamingMessageIdRef,
        isTyping,
        streamingMessageId,
        queryClient,
    } = args;
    const latestRef = useRef(args);
    latestRef.current = args;
    const [supervisor] = useState(() => createStreamSupervisor(latestRef));

    useLayoutEffect(() => {
        supervisor.setSession(sessionId ?? null);
    }, [supervisor, sessionId]);

    useEffect(() => {
        supervisor.mount();
        const onVisibilityChange = () => supervisor.onLifecycle(isPageHidden() ? 'hidden' : 'visible');
        const onFreeze = () => supervisor.onLifecycle('frozen');
        const onResume = () => supervisor.onLifecycle('resumed');
        const onOnline = () => supervisor.onLifecycle('online');
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) supervisor.onLifecycle('resumed');
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        document.addEventListener('freeze', onFreeze);
        document.addEventListener('resume', onResume);
        window.addEventListener('online', onOnline);
        window.addEventListener('pageshow', onPageShow);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            document.removeEventListener('freeze', onFreeze);
            document.removeEventListener('resume', onResume);
            window.removeEventListener('online', onOnline);
            window.removeEventListener('pageshow', onPageShow);
            supervisor.unmount();
        };
    }, [supervisor]);

    useEffect(() => {
        if (!agentId || !sessionId || isLoadingMessages || !generatingAssistantId) return;
        if ((isTyping ?? isTypingRef.current) || (streamingMessageId ?? streamingMessageIdRef.current)) return;
        // Only reconnect to a message the server actively flags as GENERATING.
        // Falling back to the newest assistant would replay the stream into an already-completed bubble.
        const target = fetchedMessagesRef.current.find(
            (m) => m.id === generatingAssistantId && m.role === 'assistant',
        );
        if (!target) return;
        supervisor.autoAttach({ agentId, sessionId, assistantId: target.id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingMessages, agentId, sessionId, sessionStreamActive, generatingAssistantId, isTyping, streamingMessageId, supervisor]);

    useEffect(() => {
        if (!agentId || !sessionId || !sessionStreamActive || generatingAssistantId || isLoadingMessages) return;
        // The session says a run is going but page 1 has no GENERATING row yet (it appears with message_start).
        const timers = SESSION_ONLY_RECHECK_MS.map((ms) =>
            setTimeout(() => {
                void refreshCommandMessagesPageOne(queryClient, agentId, sessionId);
            }, ms),
        );
        return () => timers.forEach(clearTimeout);
    }, [agentId, sessionId, sessionStreamActive, generatingAssistantId, isLoadingMessages, queryClient]);

    return supervisor;
}
